import { Link } from "react-router-dom";
import { industries } from "@/mocks/industries";
import { track } from "@/lib/analytics";

export default function Industries() {
  return (
    <section id="industries" className="relative bg-background-100 py-20 md:py-24 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="reveal max-w-3xl mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-background-300/40 px-4 py-1.5 mb-5">
            <span className="w-2 h-2 rounded-full bg-accent-400"></span>
            <span className="text-xs font-medium text-foreground-800 tracking-wide">
              Built for your industry
            </span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-medium text-foreground-950 leading-tight tracking-tight">
            The same missed enquiry,
            <br className="hidden sm:block" /> different business.
          </h2>
          <p className="mt-5 text-base text-foreground-700 leading-relaxed max-w-xl">
            Wherever a slow reply costs you money, Joble plugs the gap. Here is what it fixes for
            three of the teams that rely on it most.
          </p>
        </div>

        <div className="flex flex-col gap-5 md:gap-6">
          {industries.map((industry, index) => (
            <article
              key={industry.id}
              className="reveal group relative rounded-2xl border border-background-300/40 bg-background-50/70 p-6 md:p-8 transition-all duration-300 hover:border-primary-500/40 hover:bg-background-50"
            >
              <span className="absolute left-0 top-6 bottom-6 w-px bg-gradient-to-b from-transparent via-accent-500/40 to-transparent"></span>

              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,320px)_1fr] gap-7 lg:gap-12">
                <div className="flex lg:flex-col items-start gap-4">
                  <span className="w-12 h-12 lg:w-14 lg:h-14 shrink-0 rounded-xl flex items-center justify-center bg-primary-500/12 border border-primary-500/25">
                    <i
                      className={`${industry.icon} text-2xl text-primary-300`}
                      aria-hidden
                    ></i>
                  </span>
                  <div>
                    <span className="text-xs font-semibold tracking-[0.25em] text-foreground-500">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-xl md:text-2xl font-semibold text-foreground-950 leading-snug mt-1.5">
                      {industry.name}
                    </h3>
                    <p className="text-sm font-medium text-accent-300 leading-relaxed mt-3 border-l-2 border-accent-500/40 pl-3">
                      {industry.tagline}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_minmax(0,300px)] gap-6">
                  <div>
                    <span className="text-xs font-semibold text-foreground-600 uppercase tracking-[0.12em]">
                      What goes wrong today
                    </span>
                    <div className="mt-4 flex flex-col gap-3">
                      {industry.problems.map((problem) => (
                        <div key={problem} className="flex items-start gap-2.5">
                          <span className="w-5 h-5 mt-0.5 shrink-0 rounded-full bg-background-200/70 flex items-center justify-center">
                            <i className="ri-close-line text-xs text-foreground-400" aria-hidden></i>
                          </span>
                          <p className="text-sm text-foreground-700 leading-relaxed">{problem}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-primary-500/20 bg-primary-500/[0.07] p-5 self-start">
                    <div className="flex items-center gap-2 mb-2.5">
                      <i className="ri-flashlight-fill text-sm text-primary-400" aria-hidden></i>
                      <span className="text-xs font-semibold text-primary-300 uppercase tracking-[0.12em]">
                        How Joble helps
                      </span>
                    </div>
                    <p className="text-sm text-foreground-800 leading-relaxed">
                      {industry.solution}
                    </p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <Link
          to="/products#workspace"
          className="reveal group mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 rounded-2xl border border-primary-500/25 bg-primary-500/[0.07] p-6 md:p-7 hover:border-primary-500/45 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 shrink-0 rounded-xl flex items-center justify-center bg-primary-500/12 border border-primary-500/25">
              <i className="ri-dashboard-line text-xl text-primary-300" aria-hidden></i>
            </span>
            <div>
              <h3 className="text-base md:text-lg font-semibold text-foreground-950">
                One workspace for every industry
              </h3>
              <p className="text-sm text-foreground-700 mt-0.5">
                See how chats, calls and bookings all land in Joble Workspace.
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 text-foreground-950 text-sm font-semibold px-5 py-3 group-hover:bg-primary-600 transition-colors whitespace-nowrap shrink-0">
            Explore Joble Workspace
            <i
              className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"
              aria-hidden
            ></i>
          </span>
        </Link>

        <div className="reveal mt-12 text-center">
          <a
            href="https://app.joble.app/auth/create-account?free=true"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("try_joble_clicked", { location: "industries_section" })}
            className="group inline-flex items-center justify-center gap-3 rounded-full bg-primary-500 text-foreground-950 font-semibold text-base px-8 py-4 hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Try Joble Now
            <span className="w-7 h-7 rounded-full bg-foreground-950/15 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
              <i className="ri-arrow-right-line text-foreground-950" aria-hidden></i>
            </span>
          </a>
          <p className="mt-3 text-xs text-foreground-600">
            Live in 5 minutes. No credit card, no developer.
          </p>
        </div>
      </div>
    </section>
  );
}